import React, { createContext, useContext, useEffect, useState } from "react";
import { GuestAccount } from "../types";
import { api, setAuthToken } from "../services/api";

interface AuthContextType {
  currentUser: { username: string; id: number } | null;
  isAdmin: boolean;
  guestData: GuestAccount | null;
  signIn: (username: string) => Promise<void>;
  signOut: () => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [currentUser, setCurrentUser] = useState<{
    username: string;
    id: number;
  } | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [guestData, setGuestData] = useState<GuestAccount | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchUserData = async () => {
    try {
      const userData = await api.getCurrentUser();
      if (userData) {
        setCurrentUser({ username: userData.username, id: userData.id });
        setIsAdmin(userData.isAdmin);
        setGuestData(userData);
      }
    } catch (error) {
      console.error("Failed to fetch user data:", error);
      // Clear everything if we fail to fetch user data
      setCurrentUser(null);
      setIsAdmin(false);
      setGuestData(null);
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      try {
        await fetchUserData();
      } catch (error) {
        console.error("Failed to restore session:", error);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  const signIn = async (username: string) => {
    setLoading(true);
    try {
      const { token, user } = await api.login(username);
      setAuthToken(token);
      setCurrentUser({ username: user.username, id: user.id });
      setIsAdmin(user.isAdmin);
      setGuestData(user);
    } catch (error) {
      console.error("Login failed:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    setAuthToken(null);
    setCurrentUser(null);
    setIsAdmin(false);
    setGuestData(null);
  };

  const value = {
    currentUser,
    isAdmin,
    guestData,
    signIn,
    signOut,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
